"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FiImage } from "react-icons/fi";
import { LuLoader2 } from "react-icons/lu";
import Image from "next/image";
import { Button, Input, Label } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { queryClient } from "@/config/react-query";
import { reportScribeAttachmentLoaded } from "@/lib/scribe-attachment-analytics";
import { toast } from "@/lib/use-toast";
import { NoteAttachment } from "@/types/notes";
import { noteQueryKeys } from "@/api/provider/scribe/note-query";
import { BulkAttachmentNameUpdate, bulkUpdateNoteAttachmentNames } from "@/api/provider/scribe/notes";
interface AttachmentNameEdit {
  id: string;
  originalName: string;
  newName: string;
  thumbnailUrl: string;
  thumbnailIsCompressed: boolean;
}
interface BulkRenameAttachmentsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachments: NoteAttachment[];
  patientId?: string;
  noteId?: string | null;
}
export default function BulkRenameAttachmentsModal(t0) {
  const $ = _c(70);
  const {
    open,
    onOpenChange,
    attachments,
    patientId,
    noteId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [editedAttachments, setEditedAttachments] = useState(t1);
  const [renameAllValue, setRenameAllValue] = useState("");
  let t2;
  let t3;
  if ($[1] !== attachments || $[2] !== open) {
    t2 = () => {
      if (open && attachments && attachments.length > 0) {
        setEditedAttachments(attachments.map(_temp));
        setRenameAllValue("");
      }
    };
    t3 = [open, attachments];
    $[1] = attachments;
    $[2] = open;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== renameAllValue) {
    t4 = () => {
      if (!renameAllValue.trim()) {
        return;
      }
      setEditedAttachments(prev => prev.map(att_0 => ({
        ...att_0,
        newName: renameAllValue.trim()
      })));
    };
    $[5] = renameAllValue;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleRenameAll = t4;
  let t5;
  if ($[7] !== noteId || $[8] !== onOpenChange || $[9] !== patientId) {
    t5 = {
      mutationFn: _temp2,
      onSuccess: async updatedCount => {
        if (noteId) {
          await queryClient.invalidateQueries({
            queryKey: noteQueryKeys.attachments({
              noteId
            })
          });
        }
        if (patientId) {
          await queryClient.invalidateQueries({
            queryKey: noteQueryKeys.attachmentsInfinite({
              patientId
            })
          });
        }
        await queryClient.invalidateQueries({
          queryKey: noteQueryKeys.attachmentsInfinite({})
        });
        toast({
          title: "Success",
          description: `Updated ${updatedCount} attachment name${updatedCount !== 1 ? "s" : ""}`,
          variant: "success"
        });
        onOpenChange(false);
      },
      onError: _temp3
    };
    $[7] = noteId;
    $[8] = onOpenChange;
    $[9] = patientId;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const bulkUpdateMutation = useMutation(t5);
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (id, newName) => {
      setEditedAttachments(prev_0 => prev_0.map(att_1 => att_1.id === id ? {
        ...att_1,
        newName
      } : att_1));
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleNameChange = t6;
  let t7;
  if ($[12] !== bulkUpdateMutation || $[13] !== editedAttachments || $[14] !== onOpenChange) {
    t7 = () => {
      const updates_0 = editedAttachments.filter(_temp4).map(_temp5);
      if (updates_0.length === 0) {
        toast({
          title: "No changes",
          description: "No attachment names were modified.",
          variant: "info"
        });
        onOpenChange(false);
        return;
      }
      bulkUpdateMutation.mutate(updates_0);
    };
    $[12] = bulkUpdateMutation;
    $[13] = editedAttachments;
    $[14] = onOpenChange;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleSave = t7;
  let t8;
  if ($[16] !== editedAttachments) {
    t8 = editedAttachments.filter(_temp6);
    $[16] = editedAttachments;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const changedCount = t8.length;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DialogTitle>Rename Photos</DialogTitle>;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== attachments?.length) {
    t10 = attachments?.length || <span>{0}</span>;
    $[19] = attachments?.length;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span> selected photo</span>;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== attachments?.length) {
    t12 = attachments?.length !== 1 ? <span>{"s"}</span> : <span>{""}</span>;
    $[22] = attachments?.length;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10 || $[25] !== t12) {
    t13 = <DialogHeader>{t9}<DialogDescription>Edit the names of {t10}{t11}{t12}</DialogDescription></DialogHeader>;
    $[24] = t10;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Label htmlFor="rename-all" className="text-caption text-foreground-secondary">Rename All Photos</Label>;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e => setRenameAllValue(e.target.value);
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== renameAllValue) {
    t16 = <div className="flex-1">{t14}<Input id="rename-all" value={renameAllValue} onChange={t15} placeholder="Enter name for all photos..." className="mt-1" /></div>;
    $[29] = renameAllValue;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== renameAllValue) {
    t17 = renameAllValue.trim();
    $[31] = renameAllValue;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const t18 = !t17;
  let t19;
  if ($[33] !== handleRenameAll || $[34] !== t18) {
    t19 = <Button variant="secondary" onClick={handleRenameAll} disabled={t18} size="sm">Apply to All</Button>;
    $[33] = handleRenameAll;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t16 || $[37] !== t19) {
    t20 = <div className="mb-4 flex items-end gap-2 rounded-lg border border-dashed border-border bg-background-secondary p-3">{t16}{t19}</div>;
    $[36] = t16;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== editedAttachments) {
    let t22;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = attachment => <div key={attachment.id} className="flex items-center gap-4 rounded-lg border bg-background-secondary/50 p-3"><div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border bg-muted">{attachment.thumbnailUrl ? <Image src={attachment.thumbnailUrl} alt={attachment.originalName} fill={true} className="object-cover" unoptimized={true} onLoad={() => reportScribeAttachmentLoaded(attachment.thumbnailIsCompressed)} /> : <div className="flex h-full w-full items-center justify-center"><FiImage className="h-6 w-6 text-foreground-muted" /></div>}</div><div className="flex-1 space-y-1"><Label htmlFor={`name-${attachment.id}`} className="sr-only">Photo name</Label><Input id={`name-${attachment.id}`} value={attachment.newName} onChange={e_0 => handleNameChange(attachment.id, e_0.target.value)} placeholder="Enter photo name..." className="w-full" />{attachment.newName.trim() !== attachment.originalName && <p className="text-caption text-primary">Changed from:{" "}{attachment.originalName || <span>{"Untitled"}</span>}</p>}</div></div>;
      $[41] = t22;
    } else {
      t22 = $[41];
    }
    t21 = editedAttachments.map(t22);
    $[39] = editedAttachments;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[42] !== t21) {
    t22 = <div className="space-y-4">{t21}</div>;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t20 || $[45] !== t22) {
    t23 = <DialogBody className="max-h-[60vh] overflow-y-auto">{t20}{t22}</DialogBody>;
    $[44] = t20;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== onOpenChange) {
    t24 = () => onOpenChange(false);
    $[47] = onOpenChange;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== bulkUpdateMutation.isPending || $[50] !== t24) {
    t25 = <Button variant="secondary" onClick={t24} disabled={bulkUpdateMutation.isPending}>Cancel</Button>;
    $[49] = bulkUpdateMutation.isPending;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const t26 = bulkUpdateMutation.isPending || changedCount === 0;
  let t27;
  if ($[52] !== bulkUpdateMutation.isPending || $[53] !== changedCount) {
    t27 = bulkUpdateMutation.isPending ? <><LuLoader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : `Save ${changedCount > 0 ? `(${changedCount})` : ""}`;
    $[52] = bulkUpdateMutation.isPending;
    $[53] = changedCount;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== handleSave || $[56] !== t26 || $[57] !== t27) {
    t28 = <Button onClick={handleSave} disabled={t26}>{t27}</Button>;
    $[55] = handleSave;
    $[56] = t26;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== t25 || $[60] !== t28) {
    t29 = <DialogFooter>{t25}{t28}</DialogFooter>;
    $[59] = t25;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t13 || $[63] !== t23 || $[64] !== t29) {
    t30 = <DialogContent className="max-w-2xl">{t13}{t23}{t29}</DialogContent>;
    $[62] = t13;
    $[63] = t23;
    $[64] = t29;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== onOpenChange || $[67] !== open || $[68] !== t30) {
    t31 = <Dialog open={open} onOpenChange={onOpenChange}>{t30}</Dialog>;
    $[66] = onOpenChange;
    $[67] = open;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  return t31;
}
function _temp6(att_4) {
  return att_4.newName.trim() !== att_4.originalName;
}
function _temp5(att_3) {
  return {
    attachmentId: att_3.id,
    name: att_3.newName.trim()
  };
}
function _temp4(att_2) {
  return att_2.newName.trim() !== att_2.originalName;
}
function _temp3() {
  toast({
    title: "Error",
    description: "Failed to update attachment names. Please try again.",
    variant: "error"
  });
}
async function _temp2(updates) {
  await bulkUpdateNoteAttachmentNames(updates);
  return updates.length;
}
function _temp(att) {
  return {
    id: att.id,
    originalName: att.name || "",
    newName: att.name || "",
    thumbnailUrl: att.thumbnailSignedUrl || att.signedUrl || "",
    thumbnailIsCompressed: !!att.thumbnailSignedUrl
  };
}